import React, { useEffect, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import { createLookupApi } from "../../api/lookupApi";
import { listClassConfigurations } from "../../api/classConfigurationApi";
import { listStudents, migrateStudents } from "../../api/studentApi";
import "../Attendance/Attendance.css";

const academicYearApi = createLookupApi("academic-years");
const groupApi = createLookupApi("groups");

/**
 * Direct port of digicampusbd's `student-migration` ("Roll Wise") — bulk-
 * promote a section's students into a new academic year's section, with a
 * new roll number per student. Confirmed via `StudentMigrationService
 * .migrateStudent` that migration is create-a-new-{@code StudentIdentification}
 * -row-per-year (never update-in-place) — no fee/exam/group history is
 * copied or reset. New backend: `POST /api/students/migration/migrate`
 * (`StudentMigrationService`/`StudentIdentification.migrationStatus`/
 * `migratedStatus` fields added — the real table already had these columns,
 * just unmapped). Source roster reuses the already-exposed `GET
 * /api/students`, filtered client-side by group (matching the established
 * pattern for group filtering throughout this migration).
 */
export default function StudentMigrationPage() {
  const [academicYears, setAcademicYears] = useState([]);
  const [sourceYear, setSourceYear] = useState("");
  const [classConfigs, setClassConfigs] = useState([]);
  const [sourceClassConfigId, setSourceClassConfigId] = useState("");
  const [groups, setGroups] = useState([]);
  const [sourceGroupId, setSourceGroupId] = useState("");
  const [targetYear, setTargetYear] = useState("");
  const [targetClassConfigId, setTargetClassConfigId] = useState("");
  const [targetGroupId, setTargetGroupId] = useState("");
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    academicYearApi.list().then((years) => {
      setAcademicYears(years);
      if (years.length > 0) {
        setSourceYear(years[0].name);
        setTargetYear(years[0].name);
      }
    });
    listClassConfigurations().then((configs) => {
      setClassConfigs(configs);
      if (configs.length > 0) {
        setSourceClassConfigId(String(configs[0].id));
        setTargetClassConfigId(String(configs[0].id));
      }
    });
    groupApi.list().then((list) => {
      setGroups(list);
      if (list.length > 0) {
        setSourceGroupId(String(list[0].id));
        setTargetGroupId(String(list[0].id));
      }
    });
  }, []);

  const handleSearch = async () => {
    setError("");
    setSuccess("");
    if (!sourceYear || !sourceClassConfigId || !sourceGroupId) {
      setError("Please select the source academic year, section, and group.");
      return;
    }
    try {
      const students = await listStudents(sourceYear, sourceClassConfigId);
      const filtered = students
        .filter((s) => String(s.groupId) === String(sourceGroupId))
        .sort((a, b) => (a.studentRoll || 0) - (b.studentRoll || 0))
        .map((s) => ({ ...s, newRoll: s.studentRoll }));
      setRows(filtered);
    } catch (err) {
      setError("Failed to load students.");
    }
  };

  const updateRoll = (identificationId, value) => {
    setRows((prev) => prev.map((r) => (r.identificationId === identificationId ? { ...r, newRoll: value } : r)));
  };

  const handleMigrate = async () => {
    setError("");
    setSuccess("");
    if (!targetYear || !targetClassConfigId || !targetGroupId) {
      setError("Please select the target academic year, section, and group.");
      return;
    }
    setSubmitting(true);
    try {
      await migrateStudents({
        classConfigId: Number(targetClassConfigId),
        groupId: Number(targetGroupId),
        migrationAcademicYear: targetYear,
        studentInfos: rows.map((r) => ({ identificationId: r.identificationId, newRoll: Number(r.newRoll) })),
      });
      setSuccess(`Migrated ${rows.length} students to ${targetYear}.`);
      setRows(null);
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Migration failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Student Migration — Roll Wise</h1>
      </div>
      {error && <p style={{ color: "var(--color-danger)" }}>{error}</p>}
      {success && <p style={{ color: "var(--color-success, green)" }}>{success}</p>}

      <h3>From (current)</h3>
      <div className="attendance-filters">
        <label>
          Academic Year
          <select value={sourceYear} onChange={(e) => setSourceYear(e.target.value)}>
            {academicYears.map((y) => (
              <option key={y.id} value={y.name}>
                {y.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Section
          <select value={sourceClassConfigId} onChange={(e) => setSourceClassConfigId(e.target.value)}>
            {classConfigs.map((c) => (
              <option key={c.id} value={c.id}>
                {c.classConfigName}
              </option>
            ))}
          </select>
        </label>
        <label>
          Group
          <select value={sourceGroupId} onChange={(e) => setSourceGroupId(e.target.value)}>
            {groups.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </select>
        </label>
        <button type="button" className="btn-primary" onClick={handleSearch}>
          Load Students
        </button>
      </div>

      {rows && (
        <>
          <h3>To (new)</h3>
          <div className="attendance-filters">
            <label>
              Academic Year
              <select value={targetYear} onChange={(e) => setTargetYear(e.target.value)}>
                {academicYears.map((y) => (
                  <option key={y.id} value={y.name}>
                    {y.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Section
              <select value={targetClassConfigId} onChange={(e) => setTargetClassConfigId(e.target.value)}>
                {classConfigs.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.classConfigName}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Group
              <select value={targetGroupId} onChange={(e) => setTargetGroupId(e.target.value)}>
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="table-scroll">
            <table className="attendance-table">
              <thead>
                <tr>
                  <th>Student ID</th>
                  <th>Student Name</th>
                  <th>Current Roll</th>
                  <th>New Roll</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.identificationId}>
                    <td>{r.customStudentId}</td>
                    <td>{r.studentName}</td>
                    <td>{r.studentRoll}</td>
                    <td>
                      <input value={r.newRoll} onChange={(e) => updateRoll(r.identificationId, e.target.value)} style={{ width: 70 }} />
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={4}>No students found for this selection.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {rows.length > 0 && (
            <button type="button" className="btn-primary" onClick={handleMigrate} disabled={submitting}>
              {submitting ? "Migrating..." : `Migrate ${rows.length} Students`}
            </button>
          )}
        </>
      )}
    </MainLayout>
  );
}
